
import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import phoneValidator from '@/helpers/validators/phoneValidator'
import store from '@/store/index'
import { Column, createLeadInAmoCrm, LeadType } from '@/api/amocrm'
import config from '@root/config'

const MARKETPLACE_FORM_STORAGE_KEY = 'marketplaceForm'

type MarketplaceStoredForm = {
    name?: string
    phone?: string
    email?: string
}

export default defineComponent({
    name: 'Opz2KpiSettingsRequestModal',
    props: {
        priceLabel: {
            type: String,
            default: '',
        },
        integrationId: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: 'Настройка KPI для ОПЗ 2.0',
        },
        leadType: {
            type: String,
            default: 'Внедрение',
        },
        leadColumn: {
            type: String,
            default: 'Ожидаем оплату',
        },
        requestName: {
            type: String,
            default: 'Заявка МП на настройку ОПЗ 2.0',
        },
        formId: {
            type: String,
            default: 'form0076',
        },
        missingMessage: {
            type: String,
            default: 'Не удалось определить настройку KPI',
        },
    },
    emits: ['close', 'submitted'],
    setup(props, { emit }) {
        const host = ref<HTMLElement | null>(null)
        const root = getCurrentInstance()!.proxy as {
            $auth?: {
                isAuthenticated?: boolean
                user?: { email?: string }
            }
            $route?: {
                query?: Record<string, string | (string | null)[] | null | undefined>
                params?: Record<string, string | undefined>
            }
        }
        const form = reactive({
            name: '',
            phone: '',
            email: '',
        })
        const errors = reactive({
            name: '',
            phone: '',
            email: '',
            consent: '',
        })
        const personalDataConsent = ref(false)
        const isSubmitting = ref(false)
        const phoneMaskOptions = ref('+#*')
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
        const nameRegex = /^[A-Za-zА-Яа-яЁё]+(?: [A-Za-zА-Яа-яЁё]+)*$/
        const normalizeName = (name: string) => name.replace(/\s+/g, ' ').trim()
        const isNameValidValue = (name: string) => {
            const normalizedName = normalizeName(name)
            return normalizedName.length >= 2 && normalizedName.length <= 50 && nameRegex.test(normalizedName)
        }
        const isEmailLocked = computed(() => Boolean(getAuthEmail()))
        const isFormValid = computed(
            () =>
                isNameValidValue(form.name) &&
                !phoneValidator(form.phone) &&
                emailRegex.test(form.email.trim()) &&
                personalDataConsent.value,
        )

        const close = () => {
            if (isSubmitting.value) return
            emit('close')
        }

        const getAuthEmail = () => {
            if (!root.$auth?.isAuthenticated) return ''
            return root.$auth.user?.email || ''
        }

        const getStoredForm = (): MarketplaceStoredForm => {
            try {
                return JSON.parse(localStorage.getItem(MARKETPLACE_FORM_STORAGE_KEY) || '{}')
            } catch (e) {
                return {}
            }
        }

        const getUtmMarks = () => {
            const utmMarks: Record<string, string> = {}
            const addUtmMark = (key: string, value?: string | (string | null)[] | null) => {
                if (!key.startsWith('utm_')) return
                const normalizedValue = Array.isArray(value) ? value.find(Boolean) : value
                if (!normalizedValue) return
                utmMarks[key] = normalizedValue
                localStorage.setItem(key, normalizedValue)
            }
            Object.entries(root.$route?.query || {}).forEach(([key, value]) => addUtmMark(key, value))
            for (let index = 0; index < localStorage.length; index += 1) {
                const key = localStorage.key(index)
                if (!key?.startsWith('utm_') || utmMarks[key]) continue
                const value = localStorage.getItem(key)
                if (value) utmMarks[key] = value
            }
            return utmMarks
        }

        const clearUtmMarks = () => {
            try {
                const utmKeys: string[] = []
                for (let index = 0; index < localStorage.length; index += 1) {
                    const key = localStorage.key(index)
                    if (key?.startsWith('utm_')) utmKeys.push(key)
                }
                utmKeys.forEach(key => localStorage.removeItem(key))
            } catch (e) {
                // ignore storage access issues
            }
        }

        const changePhoneMask = (phone: string) => {
            const cleanPhone = phone?.startsWith('+') ? phone : `+${phone}`
            if (cleanPhone?.startsWith('+998') || cleanPhone?.startsWith('+971')) {
                phoneMaskOptions.value = '+###-##-#*'
            } else if (cleanPhone?.startsWith('+996')) {
                phoneMaskOptions.value = '+### ### #*'
            } else if (cleanPhone?.startsWith('+995')) {
                phoneMaskOptions.value = '+### (###) ###-#*'
            } else if (cleanPhone?.startsWith('+994')) {
                phoneMaskOptions.value = '+### ### ### ## #*'
            } else if (cleanPhone?.startsWith('+993')) {
                phoneMaskOptions.value = '+###-#-###-#*'
            } else if (cleanPhone?.startsWith('+992')) {
                phoneMaskOptions.value = '+### ## ### ## #*'
            } else if (cleanPhone?.startsWith('+380')) {
                phoneMaskOptions.value = '+### ## #*'
            } else if (cleanPhone?.startsWith('+375')) {
                phoneMaskOptions.value = '+### ## ###-##-#*'
            } else if (cleanPhone?.startsWith('+374')) {
                phoneMaskOptions.value = '+###-##-###-#*'
            } else if (cleanPhone?.startsWith('+373')) {
                phoneMaskOptions.value = '+###-####-#*'
            } else if (cleanPhone?.startsWith('+66')) {
                phoneMaskOptions.value = '+##-##-###-##-#*'
            } else if (cleanPhone?.startsWith('+62')) {
                phoneMaskOptions.value = '+## ## ### ### #*'
            } else if (cleanPhone?.startsWith('+44')) {
                phoneMaskOptions.value = '+## #### #*'
            } else if (cleanPhone?.startsWith('+7') || cleanPhone?.startsWith('+8')) {
                phoneMaskOptions.value = '+# (###) ###-##-#*'
            } else if (cleanPhone?.startsWith('+1')) {
                phoneMaskOptions.value = '+# (###) ###-#*'
            } else {
                phoneMaskOptions.value = '+#*'
            }
        }

        const hydrateForm = () => {
            const storedForm = getStoredForm()
            const authEmail = getAuthEmail()
            form.name = storedForm.name || ''
            form.phone = storedForm.phone || ''
            form.email = authEmail || storedForm.email || ''
            personalDataConsent.value = false
            changePhoneMask(form.phone.replace(/[^\d+]/g, ''))
            errors.name = ''
            errors.phone = ''
            errors.email = ''
            errors.consent = ''
        }

        const saveForm = () => {
            localStorage.setItem(
                MARKETPLACE_FORM_STORAGE_KEY,
                JSON.stringify({
                    name: normalizeName(form.name),
                    phone: form.phone.replace(/[^\d+]/g, ''),
                    email: form.email.trim(),
                }),
            )
        }

        const validateName = () => {
            const name = normalizeName(form.name)
            if (!name) errors.name = 'Поле должно быть заполнено'
            else if (!isNameValidValue(name)) {
                errors.name = 'Имя должно содержать только буквы и пробелы, от 2 до 50 символов'
            } else errors.name = ''
            return !errors.name
        }

        const validatePhone = () => {
            errors.phone = phoneValidator(form.phone) || ''
            return !errors.phone
        }

        const validateEmail = () => {
            const email = form.email.trim()
            if (!email) errors.email = 'Поле должно быть заполнено'
            else if (!emailRegex.test(email)) errors.email = 'Некорректный e-mail'
            else errors.email = ''
            return !errors.email
        }

        const validateConsent = () => {
            errors.consent = personalDataConsent.value
                ? ''
                : 'Подтвердите согласие на обработку персональных данных.'
            return !errors.consent
        }

        const onNameInput = (event: Event) => {
            const target = event.target as HTMLInputElement
            form.name = target.value
                .replace(/[^A-Za-zА-Яа-яЁё\s]/g, '')
                .replace(/\s+/g, ' ')
                .replace(/^\s/, '')
                .slice(0, 50)
            errors.name = ''
        }

        const onPhoneInput = (event: Event) => {
            const target = event.target as HTMLInputElement
            form.phone = target.value.replace('+8', '+7')
            changePhoneMask(form.phone.replace(/[^\d+]/g, ''))
            errors.phone = ''
        }

        const submit = async () => {
            if (isSubmitting.value) return
            const valid = validateName() && validatePhone() && validateEmail() && validateConsent()
            if (!valid) return
            const integrationId =
                props.integrationId ||
                String(config.marketplaceModulesIds[config.buildType]?.opz2KPISettings || '')
            if (!integrationId) {
                store.dispatch('callNotify', props.missingMessage)
                return
            }
            isSubmitting.value = true
            const projectId = root.$route?.params?.id
            try {
                await createLeadInAmoCrm({
                    name: normalizeName(form.name),
                    phone: form.phone.replace(/[^\d+]/g, ''),
                    email: form.email.trim(),
                    type: props.leadType as LeadType,
                    column: props.leadColumn as Column,
                    requestName: props.requestName,
                    note: null,
                    formId: props.formId,
                    integrationId,
                    ...(projectId ? { projectId } : {}),
                    utm: getUtmMarks(),
                    url: typeof window !== 'undefined' ? window.location.href : '',
                })
                saveForm()
                clearUtmMarks()
                isSubmitting.value = false
                emit('submitted')
            } catch (error) {
                isSubmitting.value = false
                store.dispatch('callNotify', 'Ошибка при отправке заявки. Попробуйте позже.')
            }
        }

        const onKeydown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close()
        }

        onMounted(() => {
            hydrateForm()
            const el = host.value
            if (el && el.parentNode !== document.body) document.body.appendChild(el)
            document.addEventListener('keydown', onKeydown)
        })

        onBeforeUnmount(() => {
            document.removeEventListener('keydown', onKeydown)
        })

        return {
            host,
            form,
            errors,
            personalDataConsent,
            isSubmitting,
            isFormValid,
            isEmailLocked,
            phoneMaskOptions,
            close,
            submit,
            onNameInput,
            onPhoneInput,
            validateName,
            validatePhone,
            validateEmail,
        }
    },
})
